import type { CharacterPortrait, EventStanding } from '@shefira/shared';

import { type DomAtlas, loadCharacterAtlas } from '../assets/dom-atlas.js';
import { element } from './kit.js';
import { uiFormat } from './text.js';
import { HEAD_FRAME, headPortrait } from './portrait.js';

/**
 * The lines of a world event's ranking (ADR 0050), shared by the tracker
 * and the result: rank, head, name and level, share. The heads are drawn
 * once per look and copied after: the tracker is rebuilt every second.
 */
export class PortraitCache {
  private atlas: DomAtlas | null = null;
  private readonly drawn = new Map<string, HTMLCanvasElement>();

  constructor(onReady: () => void) {
    void loadCharacterAtlas().then((atlas) => {
      this.atlas = atlas;
      onReady();
    });
  }

  /** A fresh canvas of this head, blank until the atlas has arrived. */
  head(portrait: CharacterPortrait): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = HEAD_FRAME.size;
    canvas.height = HEAD_FRAME.size;
    if (this.atlas === null) return canvas;
    const key = JSON.stringify(portrait);
    let source = this.drawn.get(key);
    if (source === undefined) {
      source = headPortrait(this.atlas, portrait);
      this.drawn.set(key, source);
    }
    canvas.getContext('2d')?.drawImage(source, 0, 0);
    return canvas;
  }
}

/** One line of the ranking; `me` lights the receiver's own. */
export function standingRow(
  line: EventStanding,
  portraits: PortraitCache,
  me: boolean,
): HTMLElement {
  const row = element('li', 'event-standing');
  if (line.podium) row.classList.add('event-standing--podium');
  if (!line.eligible) row.classList.add('event-standing--short');
  if (me) row.classList.add('event-standing--me');
  if (line.fallen) row.classList.add('event-standing--fallen');
  const frame = element('span', 'event-standing__portrait');
  frame.append(portraits.head(line));
  const name = element('span', 'event-standing__name', line.name);
  const level = line.fallen
    ? uiFormat('niv. {level} · tombé', { level: line.level })
    : uiFormat('niv. {level}', { level: line.level });
  name.append(' ', element('span', 'event-standing__level', level));
  row.append(
    element('span', 'event-standing__rank', String(line.rank)),
    frame,
    name,
    element('span', 'event-standing__share', uiFormat('{percent} %', { percent: line.percent })),
  );
  row.title = uiFormat('{name} : {score} points', { name: line.name, score: line.score });
  return row;
}

/** « 4:07 », minutes and seconds left. */
export function clockText(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, '0')}`;
}
